var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useReducer, useMemo, useRef } from 'react';
import styled from 'styled-components';
import 'react-dates/initialize';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
// @ts-ignore
import { SingleDatePickerPhrases } from 'react-dates/lib/defaultPhrases';
import { muiBankFill, muiCalendar, muiChevronLeft, muiChevronRight, } from '@ally/metronome-icons';
import usePrevious from '@ally/use-previous';
import { useAriaLive } from '@ally/use-aria-live';
import useKeyPress from '@ally/use-key-press';
import { Icon } from '../Icon';
import { Box } from '../Box';
import DatePickerWrapper from './DatePickerWrapper';
import { ErrorNotification } from '../ErrorNotification';
import { ContextualHelpNotification } from '../ContextualHelpNotification';
import { HolidayLegend } from './shared/HolidayLegend';
import { holidayReducer, HOLIDAY_ACTIONS, } from './shared/holidayReducer';
import { buildRecursiveString, findParentElement } from '../tools/utils';
import 'react-dates/lib/css/_datepicker.css';
const ButtonWrapper = styled.button `
  border: none;
  background: transparent;
`;
const ChevronIcon = React.forwardRef(({ orientation }, ref) => (React.createElement(ButtonWrapper, { ref: ref, "aria-label": orientation === 'prev' ? 'Previous Month' : 'Next Month' },
    React.createElement(Icon, { icon: orientation === 'prev' ? muiChevronLeft : muiChevronRight, size: "sm" }))));
const CalendarIcon = React.forwardRef((_, ref) => (React.createElement(Icon, { ref: ref, fill: "bluesuedeshoes", icon: muiCalendar, size: "lg" })));
const phraseOverrides = Object.assign(Object.assign({}, SingleDatePickerPhrases), { focusStartDate: 'Date Picker' });
const noop = function noop() {
    /* noop */
};
const DEFAULT_SCREEN_READER_TEXT = 'Enter date in the following format: ' +
    '2-digit month, 2-digit day and 4 digit year, or select enter ' +
    'and navigate forward to select a date using our interactive calendar.';
const SHORT_SCREEN_READER_TEXT = 'Select enter and navigate forward to select a date using our interactive calendar';
export const generateScreenReaderText = (screenReaderOpts) => {
    const combinedMessage = screenReaderOpts.reduce((acc, { type, content }) => {
        const isContextualHelpString = type === 'contextualHelp' && typeof content === 'string' && content.length;
        const isContextualHelpElement = type === 'contextualHelp' && content && React.isValidElement(content);
        const isErrorString = type === 'errorMessage' && typeof content === 'string' && content.length;
        if (isContextualHelpString || isErrorString) {
            return `${acc}, ${content}`;
        }
        if (isContextualHelpElement) {
            return `${acc}, ${buildRecursiveString(content)}`;
        }
        return acc;
    }, SHORT_SCREEN_READER_TEXT);
    if (combinedMessage === SHORT_SCREEN_READER_TEXT) {
        return DEFAULT_SCREEN_READER_TEXT;
    }
    return combinedMessage;
};
const SingleDatePickerStateless = React.forwardRef((_a, ref) => {
    var { id = 'id', onDateChange = noop, onChange, onFocusHandler, hidePlaceholder = false, ariaLabel, contextualHelpContent, isOutsideRange, isDayBlocked, errorMessage, date = null, isFederalHoliday, displayFederalHolidayText = true, blockFederalHolidays, blockWeekends, renderCalendarInfo } = _a, props = __rest(_a, ["id", "onDateChange", "onChange", "onFocusHandler", "hidePlaceholder", "ariaLabel", "contextualHelpContent", "isOutsideRange", "isDayBlocked", "errorMessage", "date", "isFederalHoliday", "displayFederalHolidayText", "blockFederalHolidays", "blockWeekends", "renderCalendarInfo"]);
    const [focus, setFocus] = useState(false);
    const wrapperRef = useRef(null);
    const calendarRef = useRef(null);
    const chevronLeftRef = useRef(null);
    const chevronRightRef = useRef(null);
    const today = moment();
    const safeDate = date || today;
    const currentMonth = moment(safeDate).month();
    const currentYear = moment(safeDate).year();
    const [displayedMonth, setDisplayedMonth] = useState();
    const [displayedYear, setDisplayedYear] = useState();
    const [displayedHolidays, setDisplayedHolidays] = useState([]);
    const [holidayState, holidayDispatch] = useReducer(holidayReducer, []);
    const { announcePolite, announceAssertive } = useAriaLive();
    const calendarButton = findParentElement(calendarRef.current, 'BUTTON');
    useKeyPress(['Escape'], () => {
        var _a;
        if ((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) {
            setFocus(false);
        }
    });
    useKeyPress(['Enter'], () => {
        var _a, _b;
        if (((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) &&
            ((_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.tagName) === 'INPUT') {
            setFocus(true);
        }
    });
    const updateRef = (instance) => {
        if (ref !== null && typeof ref === 'object') {
            ;
            ref.current =
                instance;
        }
        if (typeof ref === 'function') {
            ref(instance);
        }
    };
    function handleFocusEvent(e) {
        var _a, _b;
        const isCalendarRefTargeted = e.target === calendarRef.current;
        const areDescendantsTargeted = e.target
            ? Array.from(((_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.children) || []).includes(e.target)
            : false;
        const shouldFocusCalendarButton = isCalendarRefTargeted || areDescendantsTargeted;
        if (shouldFocusCalendarButton) {
            (_b = calendarButton === null || calendarButton === void 0 ? void 0 : calendarButton.focus) === null || _b === void 0 ? void 0 : _b.call(calendarButton);
        }
    }
    useEffect(() => {
        var _a, _b;
        /**
         * Safari has non-standard behavior in that clicking a button does not cause the button to receive focus
         * which means the onFocus event on the button is never raised. This is a workaround.
         * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
         */
        (_b = (_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener) === null || _b === void 0 ? void 0 : _b.call(_a, 'click', handleFocusEvent);
        return () => {
            var _a;
            (_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', handleFocusEvent);
        };
    }, [calendarButton]);
    const prevFocus = usePrevious(focus);
    useEffect(() => {
        if (focus)
            announceAssertive('Interactive calendar opened');
        if (prevFocus && !focus)
            announcePolite('Interactive calendar closed');
    }, [focus, prevFocus]);
    useEffect(() => {
        setDisplayedHolidays(holidayState.filter(holiday => holiday.month === (displayedMonth !== null && displayedMonth !== void 0 ? displayedMonth : currentMonth) &&
            holiday.year === (displayedYear !== null && displayedYear !== void 0 ? displayedYear : currentYear)));
    }, [holidayState, currentMonth, currentYear, displayedMonth, displayedYear]);
    useEffect(() => {
        const input = document.getElementById(id);
        updateRef(input || null);
        return () => updateRef(null);
    });
    useEffect(() => {
        const inputNode = document.getElementById(id);
        if (inputNode) {
            if (errorMessage) {
                inputNode.setAttribute('data-allytm-error', `${errorMessage}`);
            }
            else {
                inputNode.removeAttribute('data-allytm-error');
            }
        }
    }, [errorMessage, id]);
    const screenReaderInputMessage = useMemo(() => {
        return generateScreenReaderText([
            { type: 'errorMessage', content: errorMessage },
            { type: 'contextualHelp', content: contextualHelpContent },
        ]);
    }, [contextualHelpContent, errorMessage]);
    const handleDateChange = (dateObject) => {
        const pickerEl = document.getElementById(id);
        const pickerDateString = dateObject && dateObject.format('MM/DD/YYYY');
        const inputDateString = pickerEl && pickerEl.value;
        const dateString = pickerDateString || inputDateString;
        onDateChange(dateObject);
        if (onChange) {
            onChange(dateString);
        }
    };
    const handleOnClose = ({ date: onCloseDate }) => {
        if (onCloseDate) {
            calendarButton === null || calendarButton === void 0 ? void 0 : calendarButton.focus();
        }
        setDisplayedMonth(undefined);
        setDisplayedYear(undefined);
    };
    const handleFocusChange = ({ focused }) => {
        if (!focused) {
            setFocus(false);
            onFocusHandler === null || onFocusHandler === void 0 ? void 0 : onFocusHandler(false);
        }
        else if (document.activeElement === calendarButton ||
            document.activeElement === calendarRef.current) {
            setFocus(focused);
            onFocusHandler === null || onFocusHandler === void 0 ? void 0 : onFocusHandler(focused);
        }
    };
    const handleDayBlocked = (day) => {
        if (blockWeekends && day.isoWeekday() >= 6)
            return true;
        if (blockFederalHolidays && isFederalHoliday)
            return isFederalHoliday(day);
        if (isDayBlocked)
            return isDayBlocked(day);
        return false;
    };
    const handleRenderDayContents = (day) => {
        if (isFederalHoliday && isFederalHoliday(day)) {
            const isHolidayCached = holidayState.some(holiday => holiday.date.isSame(day, 'date'));
            if (!isHolidayCached) {
                holidayDispatch({ type: HOLIDAY_ACTIONS.add, payload: day });
            }
            return React.createElement(Icon, { icon: muiBankFill, size: "md" });
        }
        return day.format('D');
    };
    const renderFederalHolidayText = () => (React.createElement(HolidayLegend, { holidays: displayedHolidays.map(holiday => holiday.displayText) }));
    const handleRenderCalendarInfo = () => {
        if (renderCalendarInfo) {
            return renderCalendarInfo();
        }
        if (displayFederalHolidayText && isFederalHoliday) {
            return renderFederalHolidayText();
        }
        return '';
    };
    const onDisplayedMonthChange = (month) => {
        setDisplayedMonth(month.month());
        setDisplayedYear(month.year());
    };
    return (React.createElement(Box, { ref: wrapperRef, display: "flex", flexDirection: "column" },
        React.createElement(DatePickerWrapper, { errorMessage: errorMessage },
            React.createElement(SingleDatePicker
            // react-dates @types are not up to date and/or incorrect
            // @ts-ignore-next-line
            , Object.assign({ 
                // react-dates @types are not up to date and/or incorrect
                // @ts-ignore-next-line
                ariaLabel: ariaLabel, id: id, date: date, onDateChange: handleDateChange, focused: focus, onFocusChange: handleFocusChange, numberOfMonths: 1, showDefaultInputIcon: true, inputIconPosition: "after", screenReaderInputMessage: screenReaderInputMessage, hideKeyboardShortcutsPanel: true, onClose: handleOnClose, customInputIcon: React.createElement(CalendarIcon, { ref: calendarRef }), phrases: phraseOverrides, navPrev: React.createElement(ChevronIcon, { ref: chevronLeftRef, orientation: "prev" }), navNext: React.createElement(ChevronIcon, { ref: chevronRightRef, orientation: "next" }), placeholder: hidePlaceholder ? '' : 'mm/dd/yyyy', isOutsideRange: isOutsideRange, isDayBlocked: handleDayBlocked, renderDayContents: handleRenderDayContents, renderCalendarInfo: handleRenderCalendarInfo, onNextMonthClick: onDisplayedMonthChange, onPrevMonthClick: onDisplayedMonthChange }, props))),
        React.createElement(ErrorNotification, { errorMessage: errorMessage }),
        React.createElement(ContextualHelpNotification, { content: contextualHelpContent })));
});
export { SingleDatePickerStateless };
//# sourceMappingURL=SingleDatePickerStateless.js.map